// src/context/AuthContext.js
import { createContext, useContext, useCallback, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { clearGuestIdentity } from "../utils/localStorage";
import { clearSharedAuth, persistSharedAuth, readSharedAuthSnapshot } from "../utils/sharedAuth";

const AuthContext = createContext();
const AUTH_USER_KEY = "pixyquiz_auth_user";

function canUseMemberSpace(userData) {
  if (!userData || !userData.id || !userData.email) {
    return false;
  }

  return Boolean(
    userData.is_verified ||
      userData.is_staff ||
      userData.is_superuser ||
      userData.is_moderator
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const snapshot = readSharedAuthSnapshot();
    const token = snapshot.access;
    const storedUser = snapshot.user
      ? JSON.stringify(snapshot.user)
      : localStorage.getItem(AUTH_USER_KEY);
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            persistSharedAuth({
              access: token,
              refresh: snapshot.refresh,
              user: parsedUser,
            });
          } catch {
            setUser(decoded);
          }
        } else {
          setUser(decoded);
        }
        setIsAuthenticated(true);
      } catch (err) {
        console.error("Token invalide :", err);
        clearSharedAuth();
      }
    }
    setIsAuthReady(true);
  }, []);

  const login = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsAuthReady(true);
    persistSharedAuth({
      access: localStorage.getItem("access_token"),
      refresh: localStorage.getItem("refresh_token"),
      user: userData,
    });
    clearGuestIdentity();
  }, []);

  const logout = useCallback(() => {
    clearSharedAuth();
    setUser(null);
    setIsAuthenticated(false);
    setIsAuthReady(true);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((current) => {
      const nextUser = {
        ...(current || {}),
        ...(updates || {}),
      };
      const snapshot = readSharedAuthSnapshot();
      persistSharedAuth({
        access: snapshot.access,
        refresh: snapshot.refresh,
        user: nextUser,
      });
      return nextUser;
    });
  }, []);

  const canAccessMemberArea = canUseMemberSpace(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isAuthReady,
        canAccessMemberArea,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
